import { useSelector } from 'react-redux';
import { appRoutes } from '@/routes/routes';

/**
 * Single source of truth for "what is this user's role in the company they're
 * currently viewing" — always the active company. Never profile.role (that's
 * companyList[0].role, not necessarily the active company) and never "any role
 * this user holds in any company they belong to."
 *
 * Resolves to companyRole when present (company-wide privilege: Admin or
 * SuperAdmin) and falls back to the legacy per-company role field otherwise.
 *
 * Plain (non-hook) form, for the rare call site that can't use hooks (e.g. a
 * class component). Everything else should use the hooks below.
 */
export function getActiveCompanyRole(profile) {
  return profile?.activeCompany?.companyRole ?? profile?.activeCompany?.role;
}

export function useActiveCompanyRole() {
  return useSelector((state) => getActiveCompanyRole(state.auth?.profile));
}

export function useIsAdmin() {
  const role = useActiveCompanyRole();
  return role === 'Admin' || role === 'SuperAdmin';
}

export function useIsSuperAdmin() {
  return useActiveCompanyRole() === 'SuperAdmin';
}

export function useHasRole(...roles) {
  const role = useActiveCompanyRole();
  return roles.includes(role);
}

/**
 * Where a role should land as the app's "home"/dashboard route. Member is
 * not authorized for Department Dashboard (see routesData.jsx's
 * teamDashboard route) — they land on their own User Dashboard instead.
 * Every other role (Admin/SuperAdmin/Manager/Client) lands on Department
 * Dashboard. Single source of truth so every "send the user home" call
 * site (post-login, team switch, nav links, etc.) agrees with
 * routesData.jsx's own authorizedFor for these two routes.
 */
export function getDefaultDashboardRoute(role) {
  return role === 'Member'
    ? appRoutes().dashboardOverviewNew
    : appRoutes().teamDashboard;
}

export function useDefaultDashboardRoute() {
  return getDefaultDashboardRoute(useActiveCompanyRole());
}
